/**
 * Лёгкие анимационные примитивы лендинга — без GSAP/framer-motion,
 * чтобы держать bundle < 150KB gz (web/performance budget).
 *
 *  - useReveal: IntersectionObserver вешает .is-in на [data-reveal]
 *    потомков (классы .reveal / .reveal-line из styles.css).
 *  - useCountUp: число «доезжает» до значения когда блок виден.
 *  - useScrollProgress: 0..1 прогресс страницы для Rail.
 */
import { useEffect, useRef, useState } from "react";

const REDUCED =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Наблюдает потомков с [data-reveal] и включает им .is-in один раз.
 *
 * `deps` — если секция монтируется НЕ на первом кадре (напр. hero-fallback
 * `#process` появляется только когда `scrub` резолвится в false), эффект на
 * первом проходе видит `ref.current === null` и выходит рано, а с `[]` больше
 * НЕ перезапускается → контент навсегда `opacity:0` (пустой блок). Передай
 * сюда зависимость (напр. `[scrub]`), чтобы эффект пере-настроился, когда ref
 * реально прикрепится. По умолчанию `[]` — прежнее поведение.
 */
export function useReveal<T extends HTMLElement>(deps: readonly unknown[] = []) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const targets = Array.from(root.querySelectorAll<HTMLElement>("[data-reveal]"));
    if (targets.length === 0) return;

    const reveal = (el: Element) => el.classList.add("is-in");

    // reduced-motion ИЛИ нет IntersectionObserver → показываем сразу.
    if (REDUCED || typeof IntersectionObserver === "undefined") {
      targets.forEach(reveal);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            reveal(entry.target);
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    targets.forEach((el) => io.observe(el));

    // Пуленепробиваемый фолбэк. В части окружений (узкие desktop-окна, in-app
    // webview'ы Telegram/Instagram) IntersectionObserver не «тикает» на скролл,
    // и секция навсегда зависает на opacity:0 → на её месте пустой блок. Поэтому
    // на скролл (+ короткий интервал после mount) вручную раскрываем то, что
    // РЕАЛЬНО попало в вьюпорт. Анимацию это не ломает: раскрываем только
    // видимое, элементы ниже сгиба ждут своей очереди как и раньше.
    let stopped = false;
    const sweep = () => {
      if (stopped) return;
      const vh = window.innerHeight;
      let remaining = 0;
      for (const el of targets) {
        if (el.classList.contains("is-in")) continue;
        const r = el.getBoundingClientRect();
        if (r.top < vh * 0.92 && r.bottom > 0) reveal(el);
        else remaining++;
      }
      if (remaining === 0) {
        stopped = true;
        window.clearInterval(interval);
      }
    };

    const onScroll = () => sweep();
    window.addEventListener("scroll", onScroll, { passive: true });
    // Интервал ловит случай «загрузились и не скроллили» + мёртвый IO без скролла.
    const interval = window.setInterval(sweep, 400);
    const stopTimer = window.setTimeout(() => window.clearInterval(interval), 10000);
    sweep();

    // Безусловная страховка (per-instance, без глобального флага). Если в
    // окружении не сработали НИ IO, ни scroll, ни interval — раскрываем контент
    // этой секции через 4с, чтобы он НИКОГДА не остался невидимым (пустой блок).
    // В норме IO/скролл раскрывают раньше с анимацией; сюда доходит только «мёртвое» окружение.
    const failsafe = window.setTimeout(() => targets.forEach(reveal), 4000);

    return () => {
      stopped = true;
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.clearInterval(interval);
      window.clearTimeout(stopTimer);
      window.clearTimeout(failsafe);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}

/** Число 0→value за duration мс, стартует когда элемент попал в viewport. */
export function useCountUp(value: number, duration = 1400): {
  ref: React.RefObject<HTMLSpanElement>;
  display: number;
} {
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(REDUCED ? value : 0);

  useEffect(() => {
    if (REDUCED) {
      setDisplay(value);
      return;
    }
    const el = ref.current;
    if (!el) return;

    let raf = 0;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        const start = performance.now();
        const tick = (now: number) => {
          const p = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - p, 4);
          setDisplay(Math.round(value * eased));
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value, duration]);

  return { ref, display };
}

/** Прогресс скролла страницы 0..1 (для верхнего счётчика Rail). */
export function useScrollProgress(): number {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const doc = document.documentElement;
        const max = doc.scrollHeight - window.innerHeight;
        setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return progress;
}

export const prefersReducedMotion = REDUCED;
